import { useQueryClient } from "@tanstack/react-query";
import { Icon } from "@/components/Icon/Icon";
import { ModalShell } from "../ModalShell";
import { GHSPictogram } from "./GHSPictogram";
import { ModalLoadingSkeleton } from "./ModalLoadingSkeleton";
import { ModalErrorPanel } from "./ModalErrorPanel";
import { useSubstanceSafety } from "@/hooks/useSubstanceSafety";
import { parseSubstanceKey } from "@/utils/types/substance";
import { lookupServerCompound } from "@/utils/serverCompounds";

type SafetyModalProps = { compoundId: string; onClose: () => void };

export function SafetyModal({ compoundId, onClose }: SafetyModalProps) {
  const qc  = useQueryClient();
  const sub = parseSubstanceKey(compoundId);
  const q   = useSubstanceSafety(sub?.kind ?? null, sub?.id ?? null);
  const sc  = lookupServerCompound(compoundId);

  const shellHeader = (subtitle: string, accent: string) => ({
    open:      true as const,
    onClose,
    eyebrow:   "Safety datasheet",
    title:     sc?.name ?? "—",
    subtitle,
    accentVar: accent,
    width:     920,
  });

  if (q.isPending) {
    return (
      <ModalShell {...shellHeader("", "var(--ink-4)")}>
        <ModalLoadingSkeleton kind="safety" />
      </ModalShell>
    );
  }
  if (q.isError) {
    return (
      <ModalShell {...shellHeader("", "var(--ink-4)")}>
        <ModalErrorPanel
          onRetry={() => qc.invalidateQueries({ queryKey: ["substanceSafety"] })}
        />
      </ModalShell>
    );
  }

  const s = q.data!;
  const empty =
    s.signal === null &&
    s.pictograms.length === 0 &&
    s.hazardStatements.length === 0 &&
    s.precautionaryStatements.length === 0 &&
    s.references.length === 0;
  if (empty) {
    return (
      <ModalShell {...shellHeader(`${sc?.cas ?? ""} · No safety data on file`, "var(--ink-4)")}>
        <div style={{ padding: 32, textAlign: "center", color: "var(--ink-3)" }}>
          No safety data is available for this substance in our sources.
        </div>
      </ModalShell>
    );
  }

  const accent =
    s.signal === "Danger"
      ? "#c92619"
      : s.signal === "Warning"
      ? "#d97706"
      : "var(--ink-4)";

  return (
    <ModalShell
      {...shellHeader(`${sc?.cas ?? ""} · GHS · Signal: ${s.signal ?? "—"}`, accent)}
    >
      {/* Pictogram strip + signal */}
      <div className="safety-pictogram-strip">
        <div className="vstack" style={{ gap: 4, minWidth: 90 }}>
          <span className="label" style={{ margin: 0 }}>Signal word</span>
          <div className="safety-signal-word" style={{ color: accent }}>
            {s.signal ?? "—"}
          </div>
        </div>
        <div style={{ width: 1, alignSelf: "stretch", background: "var(--line-2)" }} />
        <div className="vstack" style={{ gap: 6, flex: 1 }}>
          <span className="label" style={{ margin: 0 }}>GHS pictograms</span>
          <div className="hstack" style={{ gap: 14, flexWrap: "wrap" }}>
            {s.pictograms.length === 0 && (
              <span style={{ fontSize: 12, color: "var(--ink-3)" }}>
                No formal GHS hazards.
              </span>
            )}
            {s.pictograms.map((p) => (
              <div key={p.code} className="ghs-pictogram-tile">
                <GHSPictogram code={p.code} size={50} />
                <span className="ghs-pictogram-code">{p.code}</span>
                <span className="ghs-pictogram-name">{p.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* H + P statements */}
      <div className="safety-statements-grid">
        <div>
          <div className="label" style={{ marginBottom: 8 }}>
            Hazard statements (H)
          </div>
          {s.hazardStatements.length === 0 && (
            <div style={{ fontSize: 12, color: "var(--ink-3)" }}>None.</div>
          )}
          <div className="vstack" style={{ gap: 6 }}>
            {s.hazardStatements.map((h) => (
              <div
                key={h.code}
                className="hstack"
                style={{ alignItems: "flex-start", gap: 10, fontSize: 12 }}
              >
                <span className="h-statement-tag">{h.code}</span>
                <span style={{ color: "var(--ink)", lineHeight: 1.5 }}>{h.text}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="label" style={{ marginBottom: 8 }}>
            Precautionary statements (P)
          </div>
          {s.precautionaryStatements.length === 0 && (
            <div style={{ fontSize: 12, color: "var(--ink-3)" }}>None.</div>
          )}
          <div className="vstack" style={{ gap: 6 }}>
            {s.precautionaryStatements.map((p) => (
              <div
                key={p.code}
                className="hstack"
                style={{ alignItems: "flex-start", gap: 10, fontSize: 12 }}
              >
                <span className="p-statement-tag">{p.code}</span>
                <span style={{ color: "var(--ink)", lineHeight: 1.5 }}>{p.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Practical info (first aid bucketed; lists for the rest) */}
      <PracticalBuckets
        title="First aid"
        items={[
          ["Skin",       s.practicalInfo.firstAid.skin],
          ["Eyes",       s.practicalInfo.firstAid.eyes],
          ["Inhalation", s.practicalInfo.firstAid.inhalation],
          ["Ingestion",  s.practicalInfo.firstAid.ingestion],
        ]}
      />
      <PracticalList title="Skin / eye / respiratory irritation" items={s.practicalInfo.skinEyeRespIrritation} />
      <PracticalList title="Handling & storage"                  items={s.practicalInfo.handlingStorage} />
      <PracticalList title="Stability & reactivity"              items={s.practicalInfo.stabilityReactivity} />

      {/* Toxicity */}
      <div className="safety-physical-grid">
        <Stat label="Acute toxicity" value={s.toxicity.ldOral ?? "Not classified"} />
        <Stat label="Flash point"    value={s.toxicity.flashPoint ?? "—"} />
        <Stat label="Melting point"  value={s.toxicity.meltingPoint ?? "—"} />
      </div>

      {/* References */}
      <div style={{ padding: "16px 20px" }}>
        <div className="label" style={{ marginBottom: 8 }}>References</div>
        <div className="hstack" style={{ gap: 18, flexWrap: "wrap" }}>
          {s.references.map((r) => (
            <div
              key={r.source}
              className="hstack"
              style={{ gap: 6, fontSize: 12, color: "var(--ink-2)" }}
            >
              <Icon name="external" size={11} />
              <span className="mono" style={{ color: "var(--ink-3)" }}>{r.source}</span>
              <span className="mono">· {r.rows}</span>
            </div>
          ))}
        </div>
      </div>
    </ModalShell>
  );
}

function PracticalBuckets({
  title,
  items,
}: {
  title: string;
  items: [string, string[]][];
}) {
  const nonEmpty = items.filter(([, arr]) => arr.length > 0);
  if (nonEmpty.length === 0) return null;
  return (
    <div style={{ padding: "0 20px 16px" }}>
      <div className="label" style={{ marginBottom: 8 }}>{title}</div>
      <div className="vstack" style={{ gap: 8 }}>
        {nonEmpty.map(([k, arr]) => (
          <div key={k}>
            <div className="mono" style={{ fontSize: 11, color: "var(--ink-3)" }}>{k}</div>
            <div style={{ fontSize: 12, color: "var(--ink)", lineHeight: 1.5 }}>
              {arr.join(" · ")}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PracticalList({ title, items }: { title: string; items: string[] }) {
  if (items.length === 0) return null;
  return (
    <div style={{ padding: "0 20px 16px" }}>
      <div className="label" style={{ marginBottom: 8 }}>{title}</div>
      <div style={{ fontSize: 12, color: "var(--ink)", lineHeight: 1.5 }}>
        {items.join(" · ")}
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="vstack" style={{ gap: 4 }}>
      <span className="label" style={{ margin: 0 }}>{label}</span>
      <span className="mono" style={{ fontSize: 12, color: "var(--ink)" }}>{value}</span>
    </div>
  );
}
